import { Card } from '../ui/card'
import { Tooltip } from '../ui/tooltip'
import { Icon } from '@blueprintjs/core'
import 'ag-grid-community/styles/ag-grid.css' // Mandatory CSS required by the Data Grid
import 'ag-grid-community/styles/ag-theme-quartz.css' // Optional Theme applied to the Data Grid
import { AgGridReact } from 'ag-grid-react' // React Data Grid Component
import classNames, { Value as ClassValue } from 'classnames'
import { agGridTextSelectionProps } from './ag_grid_options'
import { format } from 'date-fns'
import deepEqual from 'fast-deep-equal'
import { action } from 'mobx'
import { observer } from 'mobx-react-lite'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { TableName } from '../../config'
import { FilterI } from '../../constants/filter'
import { useUser, useUserValues } from '../../hooks/useUser'
import { stickyStyles } from '../../packages/blueprint-components/src/common'
import { Search } from '../../packages/blueprint-components/src/Search'
import { TableHooks } from '../../packages/blueprint-components/src/useTableHooks'
import { TooltipContent } from '../table/TooltipHeader'
import { BulkActions } from '../ui/BulkActions'
import { CardHeader } from '../ui/CardHeader'
import { DateRangeSelect, shortcuts } from '../ui/DateRangeSelect'
import { HideColumns2 } from '../ui/HideColumns'
import { Filter3, NewFilterStore, SavedFiltersStore } from './Filter3'
import { HeaderContextMenu } from './react_data_grid_columns'
import { TableStore } from './table_store'
import { TableBottomSection2 } from './TableBottomSection2'
import { datesAreEqual, useUpdateQueryParams } from './table_helpers'
import { useSharedPortal } from '../App'
import { useRegisterActiveFilterStore } from '../command-palette/active-filter-store-context'
import { money } from '../../util/formatters'
import { isNavV2Enabled } from '../../util/nav-version'

const navV2FlatTableNames = new Set<string>([
    TableName.Campaign,
    TableName.AdGroup,
    TableName.Keyword,
    TableName.Target,
    TableName.ProductAds,
    TableName.Product,
    TableName.SearchTerm,
    TableName.CampaignPlacement,
    TableName.CampaignNegativeKeyword,
    TableName.NegativeKeyword,
    TableName.NegativeTarget,
    TableName.Portfolio,
    TableName.Logs,
    'N-Grams',
])

// MER-2601: The editable Target ACoS cell scopes its write to `context.target`.
// That target must reflect the entity each row represents. Previously it was
// hardcoded to 'campaign' for every table, so editing an ad group's Target ACoS
// actually wrote at the campaign level and then stamped the value onto every ad
// group sharing that campaign_id. Each table listed here has its own per-row
// Target ACoS entity, so its edits scope to that row.
const tableNameToTarget: Record<string, GlobalContext<any>['target']> = {
    [TableName.Campaign]: 'campaign',
    [TableName.AdGroup]: 'ad_group',
    [TableName.Keyword]: 'keywords',
    [TableName.Target]: 'targets',
    [TableName.Portfolio]: 'portfolio',
}

/**
 * The Target ACoS scope for a given table. Tables without a per-row target
 * entity fall back to 'campaign' — the inherited value they display read-only.
 */
export const defaultTargetForTable = (name: string): GlobalContext<any>['target'] =>
    tableNameToTarget[name] ?? 'campaign'

/**
 * Whether the Target ACoS cell is editable on a given table. Only tables with
 * their own per-row Target ACoS entity (the ones in `tableNameToTarget`) allow
 * editing; every other table (Products, Search Terms, Placements, N-Grams, and
 * — until the backend supports per-ad writes — Product Ads) shows the inherited
 * campaign value read-only rather than letting a single edit cascade across the
 * campaign. See MER-2601.
 */
export const isTargetAcosEditable = (name: string): boolean =>
    Object.prototype.hasOwnProperty.call(tableNameToTarget, name)

export interface GlobalContext<T> {
    store: TableStore<T>
    userHook: useUserValues
    target?: 'campaign' | 'ad_group' | 'targets' | 'keywords' | 'portfolio'
    /** Whether the Target ACoS cell may be edited on this table (MER-2601). */
    targetAcosEditable?: boolean
    selectKey?: keyof T
}

export interface AgGridTableProps<T = any> {
    /** The name of the table. This is used to store the table settings
     *  in local storage as well as saved filters in the database. */
    name: string
    tableHooks: TableHooks<T>
    store: TableStore<T>
    savedFiltersStore: SavedFiltersStore
    /** API key for saved filters; legacy tables historically used a lowercase name. */
    savedFiltersTableName?: string
    columns: AgGridColumnDef<T>[]
    selectKey?: keyof T
    /**
     * Opt in to AG Grid diffing `rowData` by `selectKey` instead of array
     * position, avoiding scroll resets on a rebuilt rowData array (MER-3119).
     * Requires `selectKey` to be present and unique on every row — not true
     * for every table (e.g. a preview table whose row shape varies by ad
     * type), so this defaults to off rather than following `selectKey`.
     */
    stableRowId?: boolean
    mutableFields?: string[]

    /** Do not render an empty table card. Kept opt-in for list pages that use it. */
    hideIfNone?: boolean

    /**
     * Called for eligible plain-row clicks. Interactive descendants are
     * deliberately excluded here so every grid gets the same navigation rule.
     */
    onRowClickItem?: (item: T) => void

    /**
     * Row height in px. Defaults to 60, the standard for the core entity grids.
     * Denser tables (e.g. admin lists) can pass 48 to match the legacy mj-table.
     */
    rowHeight?: number

    /** Fill the parent height and let the grid scroll its rows internally. */
    fitParent?: boolean

    /**
     * Users drag column dividers; TableStore persists the completed width.
     * On by default: the opt-in default of false removed the resize from every
     * entity grid (MER-3660). Pass false only for a grid with fixed columns.
     */
    resizableColumns?: boolean

    className?: ClassValue
    /** Extra classes for the card itself, for pages retaining established table chrome. */
    cardClassName?: ClassValue

    /** Whether to show the date select */
    dateSelect?: boolean
    /** Whether to show the search bar */
    search?: boolean
    /** The placeholder text for the search bar */
    searchPlaceholder?: string
    /** Whether to show the row select boxes */
    columnSelection?: boolean
    /** The filters to show */
    filters?: FilterI[]
    showPerPage?: boolean
    bulkActions?: boolean
    nested?: boolean

    // bulkActions?: (v: any) => ReactNode
    showHeader?: boolean
    header?: ReactNode
    headerComponent?: ReactNode
    children?: ReactNode
    leadingActions?: ReactNode
    otherActionsLeft?: ReactNode
    otherActions?: ReactNode

    /** The earliest date from which we have data for the account */
    minDate?: Date
    fetchData?: any
    context?: Object
}

export interface AgGridColumnDef<T = any> {
    field: keyof T
    /**
     * AG Grid's native client-side sorting is disabled for our paginated tables.
     * Sortable headers use DummyHeader, which updates the table request instead.
     */
    sortable?: boolean
    /** Whether AG Grid's native column filter is available. */
    filter?: boolean
    /** API sort key when it differs from the rendered field name. */
    sortField?: string
    headerName?: string
    /** Optional rich header label, retained by legacy-column adapters. */
    headerNode?: ReactNode
    /**
     * AG Grid instantiates renderer components. `ReactNode` remains accepted for
     * older adapters that predate the component boundary; normalise at the grid edge.
     */
    headerComponent?: ReactNode | string | React.ComponentType<any>
    cellRenderer?: ReactNode | string | ((params: any) => ReactNode)
    hide?: boolean
    cellClass?: string
    headerClass?: string
    valueFormatter?: (value: { value: number }) => string
    /**
     * It is uncommon to need to use this. It is currently only used
     * in the ad accounts overview table to pass a custom marketplace
     * to the money formatter in the header totals
     */
    headerFormatter?: (value: { value: number }) => string
    align?: 'left' | 'right'
    pinned?: 'left' | 'right' | null
    width?: number
    minWidth?: number
    /** Used instead of width when the goal is to fill the remaining empty space of the grid. */
    flex?: number
    tooltip?: ReactNode
    headerPrefix?: string
}

export interface DummyHeaderProps<T> {
    column: {
        colDef: AgGridColumnDef<T>
    }
    context: GlobalContext<T>
    selectKey?: keyof T
}

export const Tooltips: Record<string, ReactNode> = {
    smart_bid: (
        <>
            Smart Bids+ automatically changes bids on a daily basis for campaigns with Smart Bids+
            enabled, using an algorithm that factors in your target ACOS, conversion rates, and the
            maximum bid adjustment.
            <br />
            <br />
            This applies to targets or keywords that have two or more orders within the specified
            lookback period.
        </>
    ),
    cpc: (
        <TooltipContent
            title="Cost per Click"
            description="spend / clicks"
            descriptionClassName="italic text-xs"
        />
    ),
    acos: (
        <TooltipContent
            title="Advertising Cost of Sale"
            description="spend / sales"
            descriptionClassName="italic text-xs"
        />
    ),
    roas: (
        <TooltipContent
            title="Return on Ad Spend"
            description="sales / spend"
            descriptionClassName="italic text-xs"
        />
    ),
    ctr: (
        <TooltipContent
            title="Click-through Rate"
            description="clicks / impressions"
            descriptionClassName="italic text-xs"
        />
    ),
    cvr: (
        <TooltipContent
            title="Conversion Rate"
            description="orders / clicks"
            descriptionClassName="italic text-xs"
        />
    ),
    cac: (
        <TooltipContent
            title="Customer Acquisition Cost"
            description="spend / orders"
            descriptionClassName="italic text-xs"
        />
    ),
    aov: (
        <TooltipContent
            title="Average Order Value"
            description="sales / orders"
            descriptionClassName="italic text-xs"
        />
    ),
    rpc: (
        <TooltipContent
            title="Revenue per Click"
            description="sales / clicks"
            descriptionClassName="italic text-xs"
        />
    ),
    target_cpc: (
        <TooltipContent title="Target Cost per Click" descriptionClassName="italic text-xs" />
    ),
    adjusted_orders: (
        <TooltipContent
            title="Adjusted Orders"
            description="orders * sales impact multiplier"
            descriptionClassName="italic text-xs"
        />
    ),
    adjusted_sales: (
        <TooltipContent
            title="Adjusted Sales"
            description="sales * sales impact multiplier"
            descriptionClassName="italic text-xs"
        />
    ),
    blended_profit: (
        <TooltipContent
            title="Blended Profit"
            description="sales * sales impact multiplier * profit margin + estimated royalties * KENP impact multiplier"
            descriptionClassName="italic text-xs"
        />
    ),
    blended_acos: (
        <TooltipContent
            title="Blended Advertising Cost of Sale"
            description="spend / blended sales"
            descriptionClassName="italic text-xs"
        />
    ),
    blended_roas: (
        <TooltipContent
            title="Blended Return on Advertising Spend"
            description="blended profit / clicks"
            descriptionClassName="italic text-xs"
        />
    ),
    blended_cvr: (
        <TooltipContent
            title="Blended Conversion Rate"
            description="adjusted orders / clicks"
            descriptionClassName="italic text-xs"
        />
    ),
    blended_cac: (
        <TooltipContent
            title="Blended Customer Acquisition Cost"
            description="spend / adjusted orders"
            descriptionClassName="italic text-xs"
        />
    ),
    blended_aov: (
        <TooltipContent
            title="Blended Average Order Value"
            description="blended profit / adjusted orders"
            descriptionClassName="italic text-xs"
        />
    ),
    blended_rpc: (
        <TooltipContent
            title="Blended Revenue per Click"
            description="blended profit / clicks"
            descriptionClassName="italic text-xs"
        />
    ),
    adjusted_pages_read: (
        <TooltipContent
            title="Adjusted Pages Read"
            description="pages read * KENP impact multiplier"
            descriptionClassName="italic text-xs"
        />
    ),
    adjusted_estimated_royalties: (
        <TooltipContent
            title="Adjusted Royalties"
            description="estimated royalties * KENP impact multiplier"
            descriptionClassName="italic text-xs"
        />
    ),
    top_of_search_impression_share: (
        <TooltipContent
            title="Top of Search Impression Share"
            descriptionClassName="italic text-xs"
        />
    ),
    active_campaigns: (
        <TooltipContent
            title="The number of enabled campaigns this product belongs to"
            descriptionClassName="italic text-xs"
        />
    ),
    active_ad_groups: (
        <TooltipContent
            title="The number of enabled ad groups this product belongs to"
            descriptionClassName="italic text-xs"
        />
    ),
    action_promotion_rules: (
        <TooltipContent
            title="The number of active smart campaign rules that this product belongs to"
            descriptionClassName="italic text-xs"
        />
    ),
    total_promotion_rules: (
        <TooltipContent
            title="The number of total smart campaign rules that this product belongs to"
            descriptionClassName="italic text-xs"
        />
    ),
}

const MaybeTooltip = ({ children, tooltip }: { children: ReactNode; tooltip?: ReactNode }) => {
    const portalContainer = useSharedPortal()

    if (!tooltip) {
        return <>{children}</>
    }

    return (
        <Tooltip
            className="flex w-full"
            hoverOpenDelay={300}
            openOnTargetFocus={false}
            portalContainer={portalContainer}
            content={
                <div style={{ maxWidth: '400px' }}>
                    <p className="my-0">{tooltip}</p>
                </div>
            }
        >
            {children}
        </Tooltip>
    )
}

export const DummyHeader = observer(
    <T,>({ column: { colDef }, context: { store } }: DummyHeaderProps<T>) => {
        const sortField = colDef.sortField ?? (colDef.field as string)
        const sortable = colDef.sortable !== false
        let total: JSX.Element | number | string = <br />
        if (store.totals?.[colDef.field] || store.totals?.[colDef.field] === 0) {
            total = store.totals?.[colDef.field] as number
            if (colDef.headerFormatter) {
                total = colDef.headerFormatter({ value: total })
            } else if (colDef.valueFormatter) {
                total = colDef.valueFormatter({ value: total })
            }
        } else if (store.totals?.[colDef.field] === null) {
            total = 0
            if (colDef.headerFormatter) {
                total = colDef.headerFormatter({ value: total })
            } else if (colDef.valueFormatter) {
                total = colDef.valueFormatter({ value: total })
            }
        }

        const sortIcon = useMemo(() => {
            if (!store.params?.sort) return null
            if (store.params?.sort === sortField) {
                return <SortIconAsc />
            } else if (store.params?.sort === `-${sortField}`) {
                return <SortIconDesc />
            }
            return null
        }, [colDef.field, store.params?.sort])

        const setSort = () => {
            if (!sortable) return
            const currentSort = store.params.sort

            if (currentSort !== sortField && currentSort !== `-${sortField}`) {
                // If we aren't sorting on this column already, set it to descending
                store.setSort(`-${sortField}`)
            } else if (currentSort === `-${sortField}`) {
                // If we are sorting on this column in desc order, then set it to asc
                store.setSort(sortField)
            } else if (currentSort === sortField) {
                // If we are sorting on this column in asc order, then clear the sort
                store.setSort(undefined)
            }
        }

        const field = String(colDef.field)
        const baseField = field.replace(/_(\d+d|lifetime)$/, '')
        let tooltip = colDef.tooltip ?? Tooltips[field] ?? Tooltips[baseField]

        return (
            <HeaderContextMenu columnName={colDef.field as string} store={store}>
                <MaybeTooltip tooltip={tooltip}>
                    <div
                        onClick={setSort}
                        className={classNames('w-full flex-col', colDef.headerClass)}
                    >
                        <div
                            className={classNames('flex items-center', {
                                'justify-end': colDef.align === 'right',
                            })}
                        >
                            {[
                                sortable && colDef.align === 'right' ? (
                                    <div
                                        key="sort-right"
                                        className="mr-1 inline-flex h-4 w-4 shrink-0 items-center justify-center"
                                    >
                                        {sortIcon}
                                    </div>
                                ) : null,
                                colDef.headerPrefix
                                    ? [
                                          <span
                                              key="prefix"
                                              className="mr-1 text-xs font-normal text-gray-400"
                                          >
                                              {colDef.headerPrefix}
                                          </span>,
                                          <React.Fragment key="label">
                                              {colDef.headerName ? colDef.headerName : field}
                                          </React.Fragment>,
                                      ]
                                    : (colDef.headerNode ??
                                      (colDef.headerName ? colDef.headerName : field)),
                                sortable && colDef.align !== 'right' ? (
                                    <div
                                        key="sort-left"
                                        className="ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center"
                                    >
                                        {sortIcon}
                                    </div>
                                ) : null,
                            ]}
                        </div>

                        <div
                            className={classNames('flex items-center', {
                                'justify-end': colDef.align === 'right',
                            })}
                        >
                            {total}
                        </div>
                    </div>
                </MaybeTooltip>
            </HeaderContextMenu>
        )
    },
)

const tableHeaderIconColor = '#91969b'

const SortIconAsc = () => (
    <Icon icon="caret-up" size={14} color={tableHeaderIconColor} aria-hidden="true" />
)
const SortIconDesc = () => (
    <Icon icon="caret-down" size={14} color={tableHeaderIconColor} aria-hidden="true" />
)

export const isInteractiveRowClickTarget = (target: EventTarget | null): boolean =>
    target instanceof Element &&
    !!target.closest('a, button, input, select, textarea, label, [data-no-row-click]')

/**
 * Without a stable row id, AG Grid can't match incoming `rowData` back to its
 * existing rows. Background polling (e.g. scheduled-run status) rebuilds the
 * `items` array on an interval even when nothing visible changed, so an
 * identity-less grid tears down and rebuilds every row on each poll — which
 * resets scroll position to the top mid-scroll. Deriving the id from
 * `selectKey` lets AG Grid diff by identity instead of array position.
 */
export const buildAgGridRowId = <T,>(
    selectKey: keyof T | undefined,
): ((params: { data: T }) => string) | undefined =>
    selectKey === undefined ? undefined : (params) => String(params.data[selectKey])

/**
 * `stableRowId` is correct only when `selectKey` is unique on every row.
 * PreviewTable.tsx broke this: it shared `keyword_id` across mixed row
 * shapes (MER-3119). TypeScript cannot check uniqueness. This function
 * finds the first violation for a dev-only warning.
 */
export const findDuplicateRowId = <T,>(items: T[], selectKey: keyof T): string | undefined => {
    const seen = new Set<string>()
    for (const row of items) {
        const id = String(row[selectKey])
        if (seen.has(id)) return id
        seen.add(id)
    }
    return undefined
}

/**
 * Returns `value`. Keeps the previous render's reference when the new
 * value is deep-equal to it. Use this for a prop like AG Grid's
 * `columnDefs`. The consumer keys real work off reference identity.
 * The producer recomputes a fresh value on every render regardless of
 * change. This hook skips that downstream work when nothing changed.
 *
 * `deepEqual` does not track visited objects, so a circular value
 * would recurse until the stack overflows. Fall back to a reference
 * check in that case, so a future circular `value` degrades to
 * "always treat as changed" instead of crashing the render.
 */
export const useStableByValue = <T,>(value: T): T => {
    const ref = useRef(value)
    let isEqual: boolean
    try {
        isEqual = deepEqual(value, ref.current)
    } catch {
        isEqual = value === ref.current
    }
    if (!isEqual) {
        ref.current = value
    }
    return ref.current
}

function AgGridTableInner<T = any>(props: AgGridTableProps<T>) {
    const {
        name,
        selectKey,
        stableRowId = false,
        className,
        cardClassName,
        tableHooks,
        store,
        columns,
        rowHeight = 60,
        fitParent,
        resizableColumns = true,
        dateSelect = true,
        search,
        searchPlaceholder,
        columnSelection,
        filters,
        showHeader,
        header,
        headerComponent,
        children,
        leadingActions,
        minDate,
        savedFiltersStore,
        savedFiltersTableName,
        otherActionsLeft,
        otherActions,
        showPerPage = true,
        bulkActions = false,
        mutableFields,
        hideIfNone = false,
        onRowClickItem,
        fetchData = {},
        nested = false,
        context = {},
    } = props
    // Hooks
    const [selectedShortcutIndex, setSelectedShortcutIndex] = useState<number | undefined>(
        undefined,
    )
    const userHook = useUser()
    const user = userHook.user
    const { search: locationSearch } = useLocation()
    const useNavV2 = useMemo(() => isNavV2Enabled(locationSearch), [locationSearch])
    const flattenForNavV2 = useNavV2 && navV2FlatTableNames.has(String(name))
    const updateQueryParams = useUpdateQueryParams()
    const newFilterStore = useMemo(() => {
        return new NewFilterStore(store, savedFiltersStore, filters, true, savedFiltersTableName)
    }, [store, filters, savedFiltersStore, savedFiltersTableName])
    const { stickyFooter: stickyFooterSection } = useMemo(() => stickyStyles(false, false), [])

    // The command palette needs the filter store of the grid on screen. The
    // registrar is a no-op outside the provider, so a standalone render is fine.
    const registerActiveFilterStore = useRegisterActiveFilterStore()
    useEffect(
        () => registerActiveFilterStore(newFilterStore),
        [registerActiveFilterStore, newFilterStore],
    )

    // `agGridColumnsWithVisibility` reads live mobx state, so it can't be
    // `useMemo`'d against `columns` alone without going stale on a real
    // column change. `useStableByValue` recomputes it every render (cheap)
    // but only changes the `columnDefs` reference AG Grid sees when the
    // content actually did — that's what avoids AG Grid's column rebuild.
    const columnsWithVisibility = useStableByValue(store.agGridColumnsWithVisibility(columns))

    let ba = undefined
    if (bulkActions) {
        ba = BulkActions
    }

    // TableStore is the single row-data owner. `tableHooks.items` is a hook
    // snapshot and can be stale when the hook writes a completed response to
    // the observable store without causing this parent to re-render.
    const items = store.items

    const getRowId = useMemo(
        () => (stableRowId ? buildAgGridRowId<T>(selectKey) : undefined),
        [stableRowId, selectKey],
    )

    const lastWarnedDuplicateId = useRef<string | undefined>(undefined)
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return
        if (!stableRowId) return

        if (selectKey === undefined) {
            console.warn(
                `AgGridTable "${name}": stableRowId is enabled but no selectKey was ` +
                    `provided, so getRowId is a no-op — AG Grid falls back to array-index ` +
                    `identity and the scroll-reset bug (MER-3119) is not actually fixed here.`,
            )
            return
        }

        const duplicateId = findDuplicateRowId(items, selectKey)
        // Only warn when the offending id changes, not on every reload/poll
        // tick that happens to re-hit the same duplicate.
        if (duplicateId !== undefined && duplicateId !== lastWarnedDuplicateId.current) {
            lastWarnedDuplicateId.current = duplicateId
            console.warn(
                `AgGridTable "${name}": stableRowId is enabled but selectKey ` +
                    `"${String(selectKey)}" is not unique across rows (duplicate id "${duplicateId}"). ` +
                    `AG Grid will misattribute or collapse rows sharing an id.`,
            )
        }
    }, [stableRowId, selectKey, items, name])

    // AG Grid refreshes cell renderers when `context`'s reference changes.
    // `useStableByValue` builds a fresh object every render (cheap) and only
    // hands AG Grid a new reference when it actually differs — this also
    // covers callers who pass their own `context` as an inline object
    // literal, without needing a separate stabilization step for it.
    const gridContext = useStableByValue({
        store,
        userHook,
        target: defaultTargetForTable(name),
        targetAcosEditable: isTargetAcosEditable(name),
        selectKey,
        ...context,
    })

    const hasWidgets =
        filters ||
        dateSelect ||
        !!bulkActions ||
        columnSelection ||
        search ||
        !!leadingActions ||
        !!otherActionsLeft ||
        !!otherActions

    const queryByDate = action(({ from, to }: { from: Date; to: Date }) => {
        const index = shortcuts.findIndex((i) => {
            return (
                datesAreEqual(i.dateRange[0] ?? new Date(), from) &&
                datesAreEqual(i.dateRange[1] ?? new Date(), to)
            )
        })

        setSelectedShortcutIndex(index === -1 ? undefined : index)

        if (store.params.from !== from) {
            store.params.from = from
        }
        if (store.params.to !== to) {
            store.params.to = to
        }
    })

    if (hideIfNone && !store.loading && items.length === 0) {
        return null
    }

    return (
        <div
            className={classNames(
                'table-wrapper',
                className,
                fitParent && 'flex h-full min-h-0 flex-col',
            )}
        >
            <Card
                className={classNames(
                    'flex flex-col p-0 bp4-card-rounded w-full',
                    flattenForNavV2 && '!rounded-none !shadow-none',
                    cardClassName,
                    fitParent && 'h-full min-h-0',
                )}
                style={{ boxShadow: nested || flattenForNavV2 ? 'none' : undefined }}
            >
                {name && showHeader && <CardHeader title={name} component={headerComponent} />}
                {header}
                <div className={fitParent ? 'flex min-h-0 flex-1 flex-col' : undefined}>
                    {children}
                    <div className={fitParent ? 'flex min-h-0 flex-1 flex-col' : undefined}>
                        {hasWidgets && (
                            <div
                                className={classNames(
                                    'flex h-[49px] items-center justify-between',
                                    fitParent && 'shrink-0',
                                )}
                            >
                                <div className="flex flex-grow items-center">
                                    {leadingActions && <div className="ml-2">{leadingActions}</div>}
                                    {dateSelect && (
                                        <div className="ml-2">
                                            <DateRangeSelect
                                                selectedShortcutIndex={selectedShortcutIndex}
                                                minDate={
                                                    minDate
                                                        ? minDate
                                                        : user?.start_date
                                                          ? new Date(user.start_date)
                                                          : undefined
                                                }
                                                value={[store.params?.from, store.params?.to]}
                                                onChange={([from, to]) => {
                                                    if (from && to) {
                                                        updateQueryParams({
                                                            from: format(from, 'yyyy-MM-dd'),
                                                            to: format(to, 'yyyy-MM-dd'),
                                                        })

                                                        queryByDate({ from, to })
                                                    }
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className="flex items-center ml-2">
                                        {filters ? (
                                            <>
                                                <Filter3 className="mr-2" store={newFilterStore} />
                                            </>
                                        ) : (
                                            <span />
                                        )}
                                    </div>
                                    {otherActionsLeft}
                                </div>
                                <div className="flex items-center">
                                    {search && (
                                        <Search
                                            className="w-full mr-2"
                                            width={290}
                                            style={{ width: '290px' }}
                                            loading={store.loading}
                                            placeholder={searchPlaceholder}
                                            value={store.params.query ?? ''}
                                            onSearch={(query: string) => {
                                                tableHooks.setQuery(query)
                                                store.deselectAll()
                                            }}
                                        />
                                    )}
                                    {otherActions}
                                    {!!bulkActions &&
                                        ba({
                                            className: 'mr-2',
                                            columns,
                                            selectKey,
                                            fetchData,
                                            params: tableHooks.params,
                                            reload: (clearSelection: any) => {
                                                tableHooks.reload()
                                                if (clearSelection) {
                                                    store.deselectAll()
                                                }
                                            },
                                            name,
                                            modelName: undefined,
                                            disabled: !(
                                                store.selection.selectedCount && mutableFields
                                            ),
                                            mutableFields,
                                            selectedValues: {
                                                safeList: Array.from(store.selection.safeList),
                                                blockList: Array.from(store.selection.blockList),
                                                all: store.selection.all,
                                                selectedCount: store.selection.selectedCount,
                                            },
                                            selectionRead: store.selectionRead,
                                            setSelectedValues: (v) => store.setSelection(v),
                                            // setSelectedValues: onSelectChange,
                                            filterOverride: null,
                                        })}
                                    {columnSelection && (
                                        <HideColumns2
                                            className="mr-2 rounded-lg"
                                            columns={columns}
                                            store={store}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                        <div
                            className={classNames('ag-theme-quartz', fitParent && 'min-h-0 flex-1')}
                            style={
                                fitParent
                                    ? undefined
                                    : {
                                          height: `max(100px, min(calc(100vh - 300px), ${
                                              items.length * rowHeight + rowHeight
                                          }px))`,
                                      }
                            }
                        >
                            <AgGridReact
                                {...agGridTextSelectionProps}
                                rowHeight={rowHeight}
                                rowData={items}
                                getRowId={getRowId}
                                columnDefs={columnsWithVisibility as any}
                                defaultColDef={{ resizable: resizableColumns }}
                                onSortChanged={(event) => {
                                    console.log('onSortChanged', event)
                                }}
                                onRowClicked={(event: any) => {
                                    if (!onRowClickItem || !event.data) return

                                    if (isInteractiveRowClickTarget(event.event?.target ?? null)) {
                                        return
                                    }

                                    onRowClickItem(event.data)
                                }}
                                loading={store.loading}
                                suppressServerSideFullWidthLoadingRow={true}
                                context={gridContext}
                                onColumnPinned={(e) => {
                                    // @ts-ignore - bad type, this is actually a string
                                    if (!e.source == 'uiColumnDragged' || !e.column) {
                                        return
                                    }

                                    let pinned: 'start' | 'end' | false =
                                        e.pinned == null
                                            ? false
                                            : e.pinned == 'left'
                                              ? 'start'
                                              : 'end'

                                    store.setPinning(e.column.getColDef().field as string, pinned)

                                    const order = e.api
                                        .getAllGridColumns()
                                        .map((c: any) => c.colDef.field)
                                    store.setColumnOrder(columns, order)
                                }}
                                onColumnMoved={(e) => {
                                    // A reorder event
                                    if (!e.finished || !e.column) {
                                        return
                                    }

                                    const order = e.api
                                        .getAllGridColumns()
                                        .map((c: any) => c.colDef.field)
                                    store.setColumnOrder(columns, order)
                                }}
                                onColumnVisible={(e) => {
                                    if (!e.column || e.source != 'uiColumnMoved') {
                                        return
                                    }

                                    store.toggleVisible(e.column.getColDef().field as string)
                                    console.log('onColumnVisible', e)
                                }}
                                onColumnResized={(e) => {
                                    if (!e.finished) {
                                        return
                                    }

                                    // A drag next to flex columns gives one event with all the
                                    // columns and `column: null`. A flex adjustment is not a
                                    // user width: a saved width removes the flex of a column.
                                    const flexed = new Set(e.flexColumns ?? [])
                                    const resized = (e.columns ?? []).filter(
                                        (column) => !flexed.has(column),
                                    )
                                    for (const column of resized) {
                                        store.setColumnWidths(
                                            column.getColDef().field as string,
                                            column.getActualWidth(),
                                        )
                                    }
                                }}
                            />
                        </div>
                        <div className={classNames('mt-2', fitParent && 'shrink-0')}>
                            <TableBottomSection2
                                paginationClassname="mx-4 mb-4"
                                stickyFooterSection={stickyFooterSection}
                                showPerPage={showPerPage}
                                perPageLabel={'per page'}
                                store={store}
                            />
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    )
}

export const AgGridTable = observer(AgGridTableInner) as React.FC<AgGridTableProps<any>>
